import {
  Tab,
  Tabs,
  Box,
  Typography,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import { Button } from 'components'
import DateRangerPicker from 'components/DateRangerPicker'
import { usePopup } from 'hooks/usePopup'
import fileDownload from 'js-file-download'
import moment from 'moment'
import React, { ChangeEvent, useState, useEffect } from 'react'
import { separatorService } from 'services/separatorService'
import { Modal } from 'shared'

import { ISeparationGroupConfig } from '../../interfaces/ISeparationGroupService'
import { settingsService } from '../../services/settingsService'
import { useStyles } from './styles'

interface IProps {
  isOpen: boolean
  handleCloseModal: () => void
}

export const DownloadExcelAnalysis = ({ isOpen, handleCloseModal }: IProps) => {
  const classes = useStyles()
  const { addPopup } = usePopup()
  const [tab, setTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState<Date>(moment().subtract(1, 'days').toDate())
  const [groups, setGroups] = useState<ISeparationGroupConfig[]>([])
  const [endDate, setEndDate] = useState<Date>(moment().subtract(1, 'days').toDate())
  const [isValidRange, setIsValidRange] = useState(true)
  const [isUniqueValid, setIsUniqueValid] = useState(true)
  const [selectedGroup, setSelectedGroup] = useState<number | undefined>(undefined)

  const handleChangeTab = (event: ChangeEvent<{}>, newValue: number) => {
    setTab(newValue)
    setIsValidRange(true)
    setIsUniqueValid(true)
  }

  const date = (param: Date): string => {
    return moment(param).format('YYYY-MM-DD')
  }

  const getGroups = async () => {
    try {
      const response = await settingsService.getSeparationGroup()
      setGroups(response)
    } catch {
      addPopup({
        title: 'Erro ao buscar grupos de separação',
        type: 'error',
      })
    }
  }

  const validateDateRange = (start: Date, end: Date, isUniqueDate: boolean | null | undefined) => {
    const today = moment().startOf('day').toDate()
    if (!isUniqueDate) {
      setIsValidRange(true)
    } else {
      const startDay = start.getDay()
      const endDay = end.getDay()
      if (start.toDateString() === today.toDateString()) {
        setIsUniqueValid(false)
        return
      }
      if (startDay === endDay) {
        setIsUniqueValid(true)
      } else {
        setIsUniqueValid(false)
      }
    }
  }

  const handleDateChange = (range: {
    startDate: Date | null
    endDate: Date | null
    isUniqueDate?: boolean | null | undefined
  }) => {
    const newStartDate = range.startDate || moment().subtract(1, 'days').toDate()
    const newEndDate = range.endDate || moment().subtract(1, 'days').toDate()
    setStartDate(newStartDate)
    setEndDate(newEndDate)
    validateDateRange(newStartDate, newEndDate, range?.isUniqueDate)
  }

  const handleDownloadExcelTimeToSeparation = async () => {
    if (!startDate || !endDate || !isValidRange) {
      addPopup({
        type: 'error',
        title: 'Datas inválidas 😢',
        description: 'Por favor, selecione uma data inicial e final válidas para continuar.',
      })
      return
    }
    setIsLoading(true)
    try {
      const data = await separatorService.downloadAverageTimeSeparators({
        scheduledDateGte: date(startDate),
        scheduledDateLte: date(endDate),
        separationGroup: selectedGroup,
        is_excel: true,
      })
      fileDownload(data, 'tempo_medio_por_pedido.xls')
      handleCloseModal()

      addPopup({
        type: 'success',
        title: 'Opa, o download foi solicitado com sucesso 🤠',
      })
      setStartDate(moment().subtract(1, 'days').toDate())
      setEndDate(moment().subtract(1, 'days').toDate())
    } catch (error: any) {
      addPopup({
        type: 'error',
        title: 'Erro ao tentar fazer o download 😢',
        description: error?.message || 'Dá um alô pro time de tech',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadExcelOrdersBySeparator = async () => {
    if (!startDate || !endDate) {
      addPopup({
        type: 'error',
        title: 'Datas inválidas 😢',
        description: 'Por favor, selecione uma data inicial e final para continuar.',
      })
      return
    }
    setIsLoading(true)
    try {
      const data = await separatorService.getOrdersBySeparatorExcel(date(startDate), date(endDate))
      fileDownload(data, 'Pedidos_por_separador.xls')
      handleCloseModal()

      addPopup({
        type: 'success',
        title: 'Opa, o download foi solicitado com sucesso 🤠',
      })
      setStartDate(moment().subtract(1, 'days').toDate())
      setEndDate(moment().subtract(1, 'days').toDate())
    } catch (error: any) {
      addPopup({
        type: 'error',
        title: 'Erro ao tentar fazer o download 😢',
        description: error?.message || 'Dá um alô pro time de tech',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadExcelTimeByLevels = async () => {
    if (!startDate) {
      addPopup({
        type: 'error',
        title: 'Data inválida 😢',
        description: 'Por favor, selecione uma data da operação para continuar.',
      })
      return
    }
    setIsLoading(true)
    try {
      const data = await separatorService.getTimeByLevelsExcel(date(startDate))
      fileDownload(data, 'tempo_por_etapas.xls')
      handleCloseModal()

      addPopup({
        type: 'success',
        title: 'Opa, o download foi solicitado com sucesso 🤠',
      })
      setStartDate(moment().subtract(1, 'days').toDate())
      setEndDate(moment().subtract(1, 'days').toDate())
    } catch (error: any) {
      addPopup({
        type: 'error',
        title: 'Erro ao tentar fazer o download 😢',
        description: error?.message || 'Dá um alô pro time de tech',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadExcelTimeBySku = async () => {
    if (!startDate) {
      addPopup({
        type: 'error',
        title: 'Data inválida 😢',
        description: 'Por favor, selecione uma data da operação para continuar.',
      })
      return
    }
    setIsLoading(true)
    try {
      const data = await separatorService.getTimeByLevelsSku(date(startDate))
      fileDownload(data, 'tempo_por_sku.xls')
      handleCloseModal()

      addPopup({
        type: 'success',
        title: 'Opa, o download foi solicitado com sucesso 🤠',
      })
      setStartDate(moment().subtract(1, 'days').toDate())
      setEndDate(moment().subtract(1, 'days').toDate())
    } catch (error: any) {
      addPopup({
        type: 'error',
        title: 'Erro ao tentar fazer o download 😢',
        description: error?.message || 'Dá um alô pro time de tech',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGroupChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedGroup(
      (event.target.value as string) === 'undefined'
        ? undefined
        : parseInt(event.target.value as string),
    )
  }

  useEffect(() => {
    if (isOpen) {
      setStartDate(moment().subtract(1, 'days').toDate())
      setEndDate(moment().subtract(1, 'days').toDate())
      setIsValidRange(true)
      setIsUniqueValid(true)
    }
  }, [isOpen])

  useEffect(() => {
    getGroups()
  }, [])

  return (
    <Modal title="Baixar uma planilha? 🤠" handleClose={handleCloseModal} open={isOpen}>
      <LinearProgress
        variant="indeterminate"
        style={{ width: '100%', opacity: isLoading ? 1 : 0 }}
      />
      <Tabs
        value={tab}
        onChange={handleChangeTab}
        centered
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Tempo médio por pedido" />
        <Tab label="Pedidos por separador" />
        <Tab label="Tempo por etapa" />
        <Tab label="Tempo por sku" />
      </Tabs>
      <Divider style={{ margin: '1rem 0' }} />
      {tab === 0 && (
        <Box>
          <Typography className={classes.subTitle}>
            Opa, é só marcar a data inicial e final da operação e baixar!
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '250px',
                marginTop: '1rem',
                gap: '1rem',
              }}
            >
              <DateRangerPicker
                onSelectedDate={handleDateChange}
                defaultStartDate={startDate}
                defaultEndDate={endDate}
                title="Período de entrega"
                isScheduledDate
              />

              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="group-select-label">Grupo de separação</InputLabel>
                <Select
                  labelId="group-select-label"
                  value={String(selectedGroup)}
                  onChange={handleGroupChange}
                  label="Grupo de separação"
                  style={{ background: '#fff' }}
                >
                  <MenuItem value="undefined">
                    <em>Todos os grupos</em>
                  </MenuItem>
                  {groups.map(group => (
                    <MenuItem key={group.separation_group} value={group.separation_group}>
                      Grupo {group.separation_group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Button
              variant="contained"
              className={classes.buttonConfigs}
              onClick={handleDownloadExcelTimeToSeparation}
              disabled={!isValidRange || isLoading}
            >
              <Typography className={classes.buttonText}>
                {isLoading ? 'Baixando...' : 'Pode baixar!'}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {tab === 1 && (
        <Box>
          <Typography className={classes.subTitle}>
            Opa, é só marcar a data inicial e final da operação e baixar!
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '300px',
                marginTop: '1rem',
              }}
            >
              <DateRangerPicker
                onSelectedDate={handleDateChange}
                defaultStartDate={startDate}
                defaultEndDate={endDate}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Button
              variant="contained"
              className={classes.buttonConfigs}
              onClick={handleDownloadExcelOrdersBySeparator}
              disabled={!isValidRange || isLoading}
            >
              <Typography className={classes.buttonText}>
                {isLoading ? 'Baixando...' : 'Pode baixar!'}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {tab === 2 && (
        <Box>
          <Typography className={classes.subTitle}>
            Opa, é só marcar a data da operação e baixar!
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '300px',
                marginTop: '1rem',
              }}
            >
              <DateRangerPicker
                onSelectedDate={handleDateChange}
                defaultStartDate={startDate}
                defaultEndDate={endDate}
                isUniqueDate={true}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Button
              variant="contained"
              className={classes.buttonConfigs}
              onClick={handleDownloadExcelTimeByLevels}
              disabled={!isValidRange || !isUniqueValid || isLoading}
            >
              <Typography className={classes.buttonText}>
                {isLoading ? 'Baixando...' : 'Pode baixar!'}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {tab === 3 && (
        <Box>
          <Typography>Para melhor fidelidade dos dados:</Typography>
          <ul>
            <li>Consideramos somente itens separados com scanner;</li>
            <li>Não são considerados os itens separados com quebra de pre picking;</li>
            <li>Não são considerados os itens que sofreram conversão.</li>
          </ul>
          <Typography className={classes.subTitle} style={{ marginTop: '1rem' }}>
            É só marcar a data da operação e baixar!
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                maxWidth: '300px',
                marginTop: '1rem',
              }}
            >
              <DateRangerPicker
                onSelectedDate={handleDateChange}
                defaultStartDate={startDate}
                defaultEndDate={endDate}
                isUniqueDate={true}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ width: '100%', margin: '1rem 0' }}
          >
            <Button
              variant="contained"
              className={classes.buttonConfigs}
              onClick={handleDownloadExcelTimeBySku}
              disabled={!isValidRange || !isUniqueValid || isLoading}
            >
              <Typography className={classes.buttonText}>
                {isLoading ? 'Baixando...' : 'Pode baixar!'}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {!isValidRange && (
        <Typography color="error" style={{ marginTop: '1rem', textAlign: 'center' }}>
          Não selecione um intervalo maior que uma semana completa (7 dias).
        </Typography>
      )}
      {!isUniqueValid && (
        <Typography color="error" style={{ marginTop: '1rem', textAlign: 'center' }}>
          Você só pode selecionar um dia por vez. (exceto sábados)
        </Typography>
      )}
    </Modal>
  )
}
